import { Badge, Button, Textarea, TextInput } from "@mantine/core";
import { IconCopy, IconLock, IconScale } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { allSteelSubCategories, screwSubCategories, type Item } from "./data";
import { categoryLabel, subTypeLabel } from "./labels";
import { calculateSteelWeight } from "./steelWeight";

interface ItemEditPanelProps {
  item: Item | null;
  onSave: (item: Item) => void;
  onCancel: () => void;
  onCopy?: (item: Item) => void;
}

function ItemEditPanel({ item, onSave, onCancel, onCopy }: ItemEditPanelProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<Item | null>(null);

  useEffect(() => {
    if (item) {
      setFormData({ ...item });
    } else {
      setFormData(null);
    }
  }, [item]);

  const handleChange = (field: keyof Item, value: string | boolean) => {
    if (!formData) return;
    setFormData((prev) => (prev ? { ...prev, [field]: value } : null));
  };

  const handleSave = () => {
    if (formData) {
      onSave({ ...formData });
    }
  };

  const handleCopy = () => {
    if (formData && onCopy) {
      onCopy(formData);
    }
  };

  const isPreset = formData?.isPreset === true;
  const isSubTyped =
    formData?.categoryId === "steel" || formData?.categoryId === "screw";

  const subTypeName = formData?.steelType
    ? formData.categoryId === "steel"
      ? allSteelSubCategories.find((s) => s.id === formData.steelType)?.name
      : screwSubCategories.find((s) => s.id === formData.steelType)?.name
    : "";
  const subTypeText = formData?.steelType
    ? subTypeLabel(t, formData.categoryId, formData.steelType, subTypeName)
    : "";

  const weightResult = useMemo(() => {
    if (formData?.categoryId === "steel" && formData?.steelType && formData?.spec) {
      return calculateSteelWeight(formData.steelType, formData.spec);
    }
    return null;
  }, [formData?.categoryId, formData?.steelType, formData?.spec]);

  return (
    <aside className="flex w-[380px] flex-col border-l border-gray-200 bg-white">
      <div className="flex items-center justify-between border-b border-gray-200 p-4">
        <h2 className="font-semibold text-gray-900">{t("itemEditor.panel.title")}</h2>
        {isPreset && (
          <Badge
            variant="light"
            color="gray"
            leftSection={<IconLock size={12} />}
          >
            {t("itemEditor.panel.presetData")}
          </Badge>
        )}
      </div>

      <div className="flex-1 space-y-5 overflow-auto p-4">
        {!formData ? (
          <div className="flex h-full flex-col items-center justify-center text-gray-500">
            <div className="space-y-2 text-center">
              <p className="text-lg">{t("itemEditor.panel.emptyTitle")}</p>
              <p className="text-sm">{t("itemEditor.panel.emptyHint")}</p>
            </div>
          </div>
        ) : (
          <>
            <TextInput
              label={t("itemEditor.field.category")}
              value={categoryLabel(t, formData.categoryId, formData.category)}
              disabled
            />

            {isSubTyped && formData.steelType && (
              <TextInput
                label={
                  formData.categoryId === "steel"
                    ? t("itemEditor.field.steelType")
                    : t("itemEditor.field.screwType")
                }
                value={`${formData.steelType} - ${subTypeText}`}
                disabled
              />
            )}

            {formData.categoryId === "steel" && formData.spec && (
              <TextInput
                label={t("itemEditor.field.sectionSpec")}
                value={formData.spec}
                disabled
              />
            )}

            {weightResult && (
              <div className="rounded-lg border border-gray-200 bg-blue-50/50 p-4">
                <div className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-900">
                  <IconScale size={16} />
                  {t("itemEditor.weight.title")}
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">{t("itemEditor.weight.area")}</span>
                    <span className="font-mono font-medium">
                      {weightResult.areaMm2.toLocaleString()} mm²
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">{t("itemEditor.weight.unitWeight")}</span>
                    <span className="font-mono font-medium text-blue-600">
                      {weightResult.unitWeightKgM} kg/m
                    </span>
                  </div>
                </div>
              </div>
            )}

            <TextInput
              label={t("itemEditor.field.name")}
              required
              value={formData.name}
              onChange={(e) => handleChange("name", e.currentTarget.value)}
              placeholder={t("itemEditor.field.namePlaceholder")}
              disabled={isPreset}
            />

            {/* <TextInput
              label="品項編號"
              required
              value={formData.code}
              onChange={(e) => handleChange("code", e.currentTarget.value)}
              placeholder="輸入品項編號"
              disabled={isPreset}
            /> */}

            <Textarea
              label={t("itemEditor.field.description")}
              value={formData.description || ""}
              onChange={(e) =>
                handleChange("description", e.currentTarget.value)
              }
              placeholder={t("itemEditor.field.descriptionPlaceholder")}
              minRows={4}
              maxLength={200}
              autosize={false}
              description={`${formData.description?.length || 0}/200`}
              disabled={isPreset}
            />
          </>
        )}
      </div>

      <div className="flex gap-3 border-t border-gray-200 p-4">
        {isPreset ? (
          <>
            <Button variant="default" fullWidth onClick={onCancel}>
              {t("itemEditor.action.close")}
            </Button>
            <Button
              fullWidth
              leftSection={<IconCopy size={16} />}
              onClick={handleCopy}
            >
              {t("itemEditor.action.copyAndEdit")}
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="default"
              fullWidth
              onClick={onCancel}
              disabled={!formData}
            >
              {t("itemEditor.action.cancel")}
            </Button>
            <Button fullWidth onClick={handleSave} disabled={!formData}>
              {t("itemEditor.action.save")}
            </Button>
          </>
        )}
      </div>
    </aside>
  );
}

export default ItemEditPanel;
